<template>
	<sg-modal
		v-model="isOpen"
		:title="modalTitle"
		:backdrop-dismiss="false"
		class="planting-modal-expanded"
	>
		<div class="ion-padding">
			<div class="flex flex-col gap-4">
				<!-- Location -->
				<form-section-header
					:label="$t('planting-details.sections.location')"
					first
				/>

				<!-- Ranch Select -->
				<sg-select-modal
					:single="data.ranchId"
					:items="ranchOptions"
					label="Ranch"
					modal-title="Select Ranch"
					placeholder="Select ranch"
					@update:single="
						(v) => {
							data.ranchId = v;
							touch('ranchId');
							// Auto-fill grower from the selected ranch; user can still
							// override the grower for this individual planting afterward.
							data.growerId = v
								? ranchesById[v]?.growerId
								: undefined;
						}
					"
				/>

				<!-- Lot Multi-Select -->
				<sg-select-modal
					v-model:multi="selectedLotIds"
					:items="lotOptions"
					label="Lot"
					modal-title="Select Lot"
					placeholder="Select lot"
				/>

				<!-- Sublot -->
				<sg-input
					v-model="data.sublot"
					label="Sublot"
					type="text"
					placeholder="Enter sublot (optional)"
					:disabled="disabled"
					@blur="touch('sublot')"
				/>

				<!-- Boundary Preview / Add -->
				<div>
					<boundary-preview-map
						v-if="data.latestBoundary"
						:boundary="data.latestBoundary"
						label="Boundary"
					/>
					<ion-label v-else class="text-sm font-medium">
						Boundary
					</ion-label>
					<div
						:class="[
							'flex flex-row gap-2',
							{ 'mt-2': data.latestBoundary },
						]"
					>
						<sg-button
							class="flex-1"
							fill="outline"
							:disabled="disabled"
							@click="openBoundaryModal"
						>
							<ion-icon slot="start" :icon="mapOutline" />
							{{
								data.latestBoundary
									? "Edit Boundary"
									: "Add Boundary"
							}}
						</sg-button>
						<sg-button
							v-if="hasBoundaryOptions"
							id="boundary-options-trigger"
							fill="outline"
							:disabled="disabled"
							@click="isBoundaryPopoverOpen = true"
						>
							<ion-icon
								slot="icon-only"
								:icon="ellipsisVertical"
							/>
						</sg-button>
					</div>
				</div>

				<!-- Boundary Options Popover -->
				<ion-popover
					:is-open="isBoundaryPopoverOpen"
					trigger="boundary-options-trigger"
					:dismiss-on-select="true"
					@did-dismiss="isBoundaryPopoverOpen = false"
				>
					<ion-content>
						<ion-list>
							<ion-item
								v-if="canRestoreLotBoundary"
								button
								@click="restoreLotBoundary"
							>
								<ion-icon slot="start" :icon="layersOutline" />
								<ion-label>Restore Lot Boundary</ion-label>
							</ion-item>
							<ion-item
								v-if="data.latestBoundary"
								button
								@click="clearBoundary"
							>
								<ion-icon
									slot="start"
									:icon="trashOutline"
									color="danger"
								/>
								<ion-label color="danger">
									Clear Boundary
								</ion-label>
							</ion-item>
						</ion-list>
					</ion-content>
				</ion-popover>

				<!-- Planting Name -->
				<div>
					<sg-input
						v-model="data.name"
						label="Planting Name*"
						type="text"
						:placeholder="
							suggestedName
								? `Default: ${suggestedName}`
								: 'Enter planting name'
						"
						:error="touched.name ? errors.name : undefined"
						:disabled="disabled"
						@input="markNameAsCustomized"
						@blur="touch('name')"
					/>
					<sg-button
						v-if="isNameCustomized && suggestedName"
						size="small"
						fill="clear"
						class="mt-1"
						:disabled="disabled"
						@click="resetToSuggestedName"
					>
						<ion-icon slot="start" :icon="sparklesOutline" />
						Use suggested name
					</sg-button>
				</div>

				<!-- General -->
				<form-section-header
					:label="$t('planting-details.sections.general')"
				/>

				<!-- ID (externalId; "external" only from our side, not the customer's) -->
				<sg-input
					v-model="data.externalId"
					label="ID"
					type="text"
					placeholder="Enter ID (optional)"
					:disabled="disabled"
				/>

				<!-- Crop Select -->
				<sg-select-modal
					:single="data.cropId"
					:items="cropOptions"
					label="Crop*"
					modal-title="Select Crop"
					placeholder="Select crop"
					:error="touched.cropId ? errors.cropId : undefined"
					@update:single="
						(v) => {
							data.cropId = v;
							touch('cropId');
						}
					"
				/>

				<!-- Type (organic status) -->
				<sg-select-modal
					:single="data.organicStatus"
					:items="organicStatusOptions"
					label="Type"
					modal-title="Select Type"
					placeholder="Select type"
					@update:single="(v) => (data.organicStatus = v)"
				/>

				<!-- Method (drives which establishment date is shown) -->
				<sg-select-modal
					:single="data.plantMethod"
					:items="plantMethodOptions"
					label="Method"
					modal-title="Select Method"
					placeholder="Select method"
					@update:single="
						(v) => {
							if (v !== undefined) data.plantMethod = v;
						}
					"
				/>

				<!-- Acres -->
				<sg-input
					v-model.number="data.acres"
					label="Acres*"
					type="number"
					placeholder="Enter acres"
					step="0.01"
					min="0"
					:error="touched.acres ? errors.acres : undefined"
					:disabled="disabled"
					@blur="touch('acres')"
				/>

				<!-- Plastic Color Select (Cal Giant only) -->
				<sg-select-modal
					v-if="isCalGiant"
					:single="plasticColor"
					:items="plasticColorOptions"
					label="Plastic Color"
					modal-title="Select Plastic Color"
					placeholder="Select plastic color"
					@update:single="(v) => (plasticColor = v)"
				/>

				<!-- Dates -->
				<form-section-header
					:label="$t('planting-details.sections.dates')"
				/>

				<!-- Plant Date (Seed method) -->
				<div v-if="!isTransplant">
					<sg-datetime-button
						label="Plant Date*"
						datetime="planting-plant-date"
						:error="
							touched.plantDate ? errors.plantDate : undefined
						"
					/>
					<date-modal
						v-model="plantDateModel"
						datetime-id="planting-plant-date"
						title="Choose Plant Date"
						:min="DATE_RANGE.min"
						:max="DATE_RANGE.max"
						@did-dismiss="touch('plantDate')"
					/>
				</div>

				<!-- Transplant Date (Transplant method) -->
				<div v-if="isTransplant">
					<sg-datetime-button
						label="Transplant Date*"
						datetime="planting-transplant-date"
						:error="
							touched.transplantDate
								? errors.transplantDate
								: undefined
						"
					/>
					<date-modal
						v-model="transplantDateModel"
						datetime-id="planting-transplant-date"
						title="Choose Transplant Date"
						:min="DATE_RANGE.min"
						:max="DATE_RANGE.max"
						@did-dismiss="touch('transplantDate')"
					/>
				</div>

				<!-- Wet Date -->
				<div>
					<sg-datetime-button
						label="Wet Date*"
						datetime="planting-wet-date"
						:error="touched.wetDate ? errors.wetDate : undefined"
					/>
					<date-modal
						v-model="wetDateModel"
						datetime-id="planting-wet-date"
						title="Choose Wet Date"
						:min="DATE_RANGE.min"
						:max="DATE_RANGE.max"
						@did-dismiss="touch('wetDate')"
					/>
				</div>

				<!-- Harvest Date (single value UI -> array in schema) -->
				<div class="flex flex-row gap-2">
					<div class="flex-1">
						<sg-datetime-button
							label="Harvest Date (Optional)"
							datetime="planting-harvest-date"
							placeholder="Click to select harvest date"
							:has-value="!!harvestDateModel"
							:error="
								touched.harvestDates
									? errors.harvestDates
									: undefined
							"
						/>
					</div>
					<sg-button
						v-if="data.harvestDates.length > 0"
						fill="outline"
						color="danger"
						class="mt-[29px]"
						size="small"
						:disabled="disabled"
						@click="clearHarvestDate"
					>
						Clear
					</sg-button>
				</div>
				<date-modal
					v-model="harvestDateModel"
					datetime-id="planting-harvest-date"
					title="Choose Harvest Date"
					:min="DATE_RANGE.min"
					:max="DATE_RANGE.max"
					@did-dismiss="touch('harvestDates')"
				/>

				<!-- End Date -->
				<div>
					<sg-datetime-button
						label="End Date (Optional)"
						datetime="planting-end-date"
						placeholder="Click to select end date"
						:has-value="!!endDateModel"
					/>
					<date-modal
						v-model="endDateModel"
						datetime-id="planting-end-date"
						title="Choose End Date"
						:min="DATE_RANGE.min"
						:max="DATE_RANGE.max"
					/>
				</div>

				<!-- Growing -->
				<form-section-header
					:label="$t('planting-details.sections.growing')"
				/>

				<!-- Grower Select (auto-filled from ranch, overridable) -->
				<sg-select-modal
					:single="data.growerId"
					:items="growerOptions"
					label="Grower"
					modal-title="Select Grower"
					placeholder="Select grower"
					@update:single="(v) => (data.growerId = v)"
				/>

				<!-- Nursery Select -->
				<sg-select-modal
					:single="data.nurseryId"
					:items="nurseryOptions"
					label="Nursery"
					modal-title="Select Nursery"
					placeholder="Select nursery"
					@update:single="(v) => (data.nurseryId = v)"
				/>

				<!-- Rotation -->
				<sg-input
					v-model.number="data.rotation"
					label="Rotation"
					type="number"
					min="0"
					step="1"
					placeholder="Enter rotation (optional)"
					:disabled="disabled"
				/>

				<!-- Bed Configuration -->
				<form-section-header
					:label="$t('planting-details.sections.bed-configuration')"
				/>
				<div class="grid grid-cols-2 gap-4">
					<sg-input
						v-model.number="data.bedCount"
						:label="$t('planting.bed-count')"
						type="number"
						min="0"
						step="1"
						:error="touched.bedCount ? errors.bedCount : undefined"
						:disabled="disabled"
						@blur="touch('bedCount')"
					/>
					<sg-input
						v-model.number="data.bedLines"
						:label="$t('planting.bed-lines')"
						type="number"
						min="0"
						step="1"
						:error="touched.bedLines ? errors.bedLines : undefined"
						:disabled="disabled"
						@blur="touch('bedLines')"
					/>
					<sg-input
						v-model.number="data.bedSizeInches"
						:label="`${$t('planting-details.bed-size')} (in)`"
						type="number"
						min="0"
						step="any"
						:error="
							touched.bedSizeInches
								? errors.bedSizeInches
								: undefined
						"
						:disabled="disabled"
						@blur="touch('bedSizeInches')"
					/>
					<sg-input
						v-model.number="data.plantSpacingInches"
						:label="`${$t('planting.plant-spacing')} (in)`"
						type="number"
						min="0"
						step="any"
						:error="
							touched.plantSpacingInches
								? errors.plantSpacingInches
								: undefined
						"
						:disabled="disabled"
						@blur="touch('plantSpacingInches')"
					/>
					<sg-select-modal
						:single="selectedBedDirection"
						:items="BED_DIRECTION_OPTIONS"
						:label="$t('planting.bed-direction')"
						modal-title="Select Bed Direction"
						placeholder="Select direction"
						@update:single="(v) => (selectedBedDirection = v)"
					/>
				</div>
			</div>
		</div>

		<template #footer>
			<div class="flex flex-row gap-2">
				<sg-button
					color="medium"
					class="flex-1"
					:disabled="disabled"
					@click="closeModal"
				>
					{{ $t("common.cancel") }}
				</sg-button>
				<sg-button
					class="flex-1"
					:loading="isLoading"
					:disabled="disabled"
					@click="submit"
				>
					{{ $t("common.save") }}
				</sg-button>
			</div>
		</template>
	</sg-modal>

	<!-- Boundary Edit Modal -->
	<boundary-edit-modal
		ref="boundaryModalRef"
		:geometry="data.latestBoundary"
		:default-center="boundaryDefaultCenter"
		title="Edit Planting Boundary"
		@save="handleBoundarySave"
	/>
</template>

<script setup lang="ts">
	import { ref, computed, watch } from "vue";
	import {
		IonIcon,
		IonPopover,
		IonContent,
		IonList,
		IonItem,
		IonLabel,
		alertController,
	} from "@ionic/vue";
	import {
		mapOutline,
		sparklesOutline,
		ellipsisVertical,
		layersOutline,
		trashOutline,
	} from "ionicons/icons";
	import SgInput from "@/components/ui/SgInput.vue";
	import SgButton from "@/components/ui/SgButton.vue";
	import SgModal from "@/components/ui/modals/SgModal.vue";
	import SgSelectModal from "@/components/ui/modals/SgSelectModal.vue";
	import FormSectionHeader from "@/components/ui/FormSectionHeader.vue";
	import SgDatetimeButton from "@/components/ui/SgDatetimeButton.vue";
	import DateModal from "@/components/ui/modals/DateModal.vue";
	import BoundaryEditModal from "@/components/ui/modals/BoundaryEditModal.vue";
	import BoundaryPreviewMap from "@/components/ui/maps/BoundaryPreviewMap.vue";
	import { useRanches } from "seedgreen-shared/queries/ranch";
	import { useLots } from "seedgreen-shared/queries/lot";
	import type { Polygon, MultiPolygon } from "geojson";
	import {
		BoundaryType,
		PlantMethod,
		PlantMethodLabels,
		OrganicStatus,
		OrganicStatusLabels,
		type PlantingDTO,
	} from "seedgreen-shared/dtos/PlantingDTO";
	import {
		BED_DIRECTION_OPTIONS,
		bedDirectionSelection,
	} from "@/utils/planting-bed-direction";
	import {
		getCurrentDateString,
		type DateString,
	} from "seedgreen-shared/types/datestring.types";
	import { useCrops } from "seedgreen-shared/queries/crop";
	import { useNurseries } from "seedgreen-shared/queries/nursery";
	import { useGrowers } from "seedgreen-shared/queries/grower";
	import { useMyUser } from "seedgreen-shared/queries/user";
	import { CUSTOMERS } from "seedgreen-shared/constants/customers";
	import {
		PLASTIC_COLORS,
		type PlasticColor,
		getPlasticColorSelection,
		setPlasticColor,
	} from "@/utils/cal-giant/planting-custom-data";
	import {
		plantingSchema,
		type PlantingFormData,
	} from "@/validation/planting.schema";
	import { structuredValidate } from "@/utils/validation";
	import { center, booleanEqual, multiPolygon } from "@turf/turf";
	import { useRanchBoundaries } from "@/composables/useRanchBoundaries";
	import _ from "lodash";

	const props = withDefaults(
		defineProps<{
			isLoading?: boolean;
		}>(),
		{ isLoading: false },
	);

	/** Modal open state */
	const isOpen = ref(false);

	/** UI form state mirrors schema keys used in this modal */
	const DATE_RANGE = { min: "1900-01-01", max: "2100-12-31" } as const;

	const createEmptyForm = (): PlantingFormData => ({
		customerId: undefined,
		name: "",
		ranchId: undefined,
		lotIds: [],
		sublot: "",
		cropId: undefined,
		nurseryId: undefined,
		growerId: undefined,
		acres: undefined,
		wetDate: getCurrentDateString(),
		plantDate: getCurrentDateString(),
		harvestDates: [],
		harvestDatesActual: [],
		latestBoundary: undefined,
		latestBoundaryDate: getCurrentDateString(),
		latestBoundaryType: BoundaryType.UserCreated,
		isVisible: true,
		customData: undefined,
		externalId: "",
		organicStatus: OrganicStatus.Conventional,
		plantMethod: PlantMethod.Seed,
		transplantDate: undefined,
		endDate: undefined,
		rotation: undefined,
		bedCount: undefined,
		bedSizeInches: undefined,
		bedLines: undefined,
		plantSpacingInches: undefined,
		bedDirection: undefined,
	});

	const data = ref<PlantingFormData>(createEmptyForm());

	/** Errors */
	const errors = ref<Record<string, string | undefined>>({
		name: undefined,
		cropId: undefined,
		wetDate: undefined,
		plantDate: undefined,
		transplantDate: undefined,
		acres: undefined,
		harvestDates: undefined,
		bedCount: undefined,
		bedSizeInches: undefined,
		bedLines: undefined,
		plantSpacingInches: undefined,
	});

	/** Tracks which fields the user has interacted with ("touched").
		This prevents validation errors from appearing 
		before the user engages with the form fields and interacts with the form fields,
		which prevents confusing validation errors from showing
	 */
	const touched = ref({
		name: false,
		cropId: false,
		wetDate: false,
		plantDate: false,
		transplantDate: false,
		acres: false,
		harvestDates: false,
		ranchId: false,
		sublot: false,
		bedCount: false,
		bedSizeInches: false,
		bedLines: false,
		plantSpacingInches: false,
	});

	const disabled = computed(() => !!props.isLoading);
	const isEditing = ref(false);
	const modalTitle = computed(() =>
		isEditing.value ? "Edit Planting" : "Add Planting",
	);

	/** Options */
	const { ranches, ranchesById } = useRanches();
	const { lots, lotsById } = useLots();
	const { crops } = useCrops();
	const { nurseries } = useNurseries();
	const { growers } = useGrowers();
	const { myUser } = useMyUser();
	const { getRanchBoundary } = useRanchBoundaries();

	/**
	 * Cal-Giant-specific "Plastic Color" (stored in CustomData). Gated to CG by the
	 * planting's own customer when editing, falling back to the current user's
	 * customer when creating. Kept as local state (not part of PlantingFormData) and
	 * serialized into `data.customData` only on an actual change (see submit()).
	 */
	const isCalGiant = computed(
		() =>
			(data.value.customerId ?? myUser.value?.customerId) ===
			CUSTOMERS.CalGiant,
	);
	const plasticColorOptions = PLASTIC_COLORS.map((c) => ({ id: c, text: c }));
	const plasticColor = ref<PlasticColor | undefined>();
	const initialPlasticColor = ref<PlasticColor | undefined>();

	/** Type (organic status) and Method (plant method) dropdown options. */
	const organicStatusOptions = Object.entries(OrganicStatusLabels).map(
		([id, text]) => ({ id: Number(id) as OrganicStatus, text }),
	);
	const plantMethodOptions = [
		{ id: PlantMethod.Seed, text: PlantMethodLabels[PlantMethod.Seed] },
		{
			id: PlantMethod.Transplant,
			text: PlantMethodLabels[PlantMethod.Transplant],
		},
	];

	/**
	 * When the method is Transplant we collect a Transplant Date instead of a
	 * Plant Date (and vice-versa); only the date matching the method is stored.
	 */
	const isTransplant = computed(
		() => data.value.plantMethod === PlantMethod.Transplant,
	);

	/**
	 * Bed direction is stored as degrees but chosen as a cardinal direction. Kept
	 * as local state seeded from the canonical value; a legacy non-canonical value
	 * shows nothing selected and is preserved unless the user actually changes it
	 * (see submit()).
	 */
	const selectedBedDirection = ref<number | undefined>();
	const initialBedDirection = ref<number | undefined>();

	const ranchOptions = computed(() =>
		_.sortBy(ranches.value || [], (r) => r.name).map((r) => ({
			id: r.id,
			text: r.fullName,
		})),
	);

	const lotOptions = computed(() => {
		const all = lots.value || [];
		const filtered = data.value.ranchId
			? all.filter((l) => l.ranchId === data.value.ranchId)
			: all;
		return filtered.map((l) => ({ id: l.id, text: l.fullName }));
	});

	const nurseryOptions = computed(() =>
		(nurseries.value || []).map((nursery) => ({
			id: nursery.id,
			text: nursery.name,
		})),
	);

	const growerOptions = computed(() =>
		(growers.value || []).map((grower) => ({
			id: grower.id,
			text: grower.growerName,
		})),
	);

	const cropOptions = computed(() =>
		(crops.value || []).map((crop) => ({
			id: crop.id,
			text: crop.fullName,
		})),
	);

	/** Multi-select Set bridge */
	const selectedLotIds = computed<Set<number>>({
		get: () => new Set(data.value.lotIds),
		set: (s) => {
			data.value.lotIds = Array.from(s);
		},
	});

	/** Wet date model (string <-> branded) */
	const wetDateModel = computed<DateString | undefined>({
		get: () => data.value.wetDate as DateString,
		set: (v) => {
			data.value.wetDate = v as string;
			// If wet date is before plant date, move plant date to match
			if (v && data.value.plantDate && v < data.value.plantDate) {
				data.value.plantDate = v as string;
			}
		},
	});

	/** Plant date model (string <-> branded) */
	const plantDateModel = computed<DateString | undefined>({
		get: () => data.value.plantDate as DateString,
		set: (v) => {
			data.value.plantDate = v as string;
			// If plant date is after wet date, move wet date to match
			if (v && data.value.wetDate && v > data.value.wetDate) {
				data.value.wetDate = v as string;
			}
		},
	});

	/** Transplant date model (string <-> branded). Shown when method is Transplant. */
	const transplantDateModel = computed<DateString | undefined>({
		get: () => data.value.transplantDate as DateString,
		set: (v) => {
			data.value.transplantDate = v as string;
		},
	});

	/** End date model (string <-> branded) */
	const endDateModel = computed<DateString | undefined>({
		get: () => data.value.endDate as DateString,
		set: (v) => {
			data.value.endDate = v as string;
		},
	});

	/** Harvest date model (single value UI -> array) */
	const harvestDateModel = computed<DateString | undefined>({
		get: () => data.value.harvestDates[0] as DateString,
		set: (v) => {
			data.value.harvestDates = v ? [v as string] : [];
		},
	});

	/** Suggested name (LotName + Sublot) */
	const isNameCustomized = ref(false);
	const suggestedName = computed(() => {
		const lot = lotsById.value?.[data.value.lotIds[0]];
		if (!lot?.name) return "";

		const sublot = data.value.sublot?.trim();
		return sublot ? `${lot.name} ${sublot}` : lot.name;
	});

	watch(suggestedName, (s) => {
		if (!isNameCustomized.value && s) data.value.name = s;
	});

	function markNameAsCustomized() {
		isNameCustomized.value = true;
	}

	function resetToSuggestedName() {
		data.value.name = suggestedName.value;
		isNameCustomized.value = false;
	}

	/** When ranch changes, remove lots not in that ranch */
	watch(
		() => data.value.ranchId,
		(rid) => {
			if (!rid || data.value.lotIds.length === 0) return;
			const valid = data.value.lotIds.filter(
				(id) => lotsById.value?.[id]?.ranchId === rid,
			);
			if (valid.length !== data.value.lotIds.length)
				data.value.lotIds = valid;
		},
	);

	/** Helpers */
	function clearErrors() {
		errors.value.name = undefined;
		errors.value.cropId = undefined;
		errors.value.wetDate = undefined;
		errors.value.plantDate = undefined;
		errors.value.transplantDate = undefined;
		errors.value.acres = undefined;
		errors.value.harvestDates = undefined;
		errors.value.bedCount = undefined;
		errors.value.bedSizeInches = undefined;
		errors.value.bedLines = undefined;
		errors.value.plantSpacingInches = undefined;
	}
	function clearTouched() {
		touched.value.name = false;
		touched.value.cropId = false;
		touched.value.wetDate = false;
		touched.value.plantDate = false;
		touched.value.transplantDate = false;
		touched.value.acres = false;
		touched.value.harvestDates = false;
		touched.value.ranchId = false;
		touched.value.sublot = false;
		touched.value.bedCount = false;
		touched.value.bedSizeInches = false;
		touched.value.bedLines = false;
		touched.value.plantSpacingInches = false;
	}

	function hydrate(from?: PlantingDTO | null) {
		clearErrors();
		clearTouched();
		isNameCustomized.value = false;

		if (from) {
			data.value = {
				customerId: from.customerId,
				name: from.name ?? "",
				ranchId: from.ranchId,
				lotIds: [...from.lotIds],
				sublot: from.sublotCurrent ?? "",
				cropId: from.cropId ?? from.cropIdActual,
				nurseryId: from.nurseryId ?? from.nurseryIdActual,
				growerId: from.growerId,
				acres: from.acresCurrent,
				wetDate: from.wetDateCurrent,
				plantDate: from.plantDateCurrent ?? from.wetDateCurrent,
				harvestDates: from.harvestDatesCurrent as string[],
				harvestDatesActual: [],
				latestBoundary: from.latestBoundary,
				latestBoundaryDate: from.latestBoundaryDate as string,
				latestBoundaryType: from.latestBoundaryType,
				isVisible: true,
				customData: from.customData,
				externalId: from.externalId ?? "",
				organicStatus: from.organicStatus,
				plantMethod: from.plantMethod ?? PlantMethod.Seed,
				transplantDate: from.transplantDateCurrent,
				endDate: from.endDateCurrent,
				rotation: from.rotationCurrent,
				bedCount: from.bedCountCurrent,
				bedSizeInches: from.bedSizeInchesCurrent,
				bedLines: from.bedLinesCurrent,
				plantSpacingInches: from.plantSpacingInchesCurrent,
				bedDirection: from.bedDirection,
			};
			const existingName = (from.name || "").trim();
			if (existingName && existingName !== suggestedName.value)
				isNameCustomized.value = true;
		} else {
			data.value = createEmptyForm();
		}
	}

	function validate(): boolean {
		clearErrors();

		// Frontend guarantee: name required (even if schema allows empty)
		if (!data.value.name || !data.value.name.trim()) {
			errors.value.name = "Planting name is required";
		}

		// Frontend guarantee: the establishment date shown for the current method
		// (Plant Date for Seed, Transplant Date for Transplant) is required.
		if (isTransplant.value) {
			if (!data.value.transplantDate) {
				errors.value.transplantDate = "Transplant date is required";
			}
		} else if (!data.value.plantDate) {
			errors.value.plantDate = "Plant date is required";
		}

		const res = structuredValidate(plantingSchema, data.value);
		if (!res.success) {
			const e = res.errors;
			// keep already-set errors if present
			errors.value.name = errors.value.name || e.name;
			errors.value.plantDate = errors.value.plantDate || e.plantDate;
			errors.value.cropId = e.cropId;
			errors.value.wetDate = e.wetDate;
			errors.value.acres = e.acres;
			errors.value.harvestDates = e.harvestDates;
			errors.value.bedCount = e.bedCount;
			errors.value.bedSizeInches = e.bedSizeInches;
			errors.value.bedLines = e.bedLines;
			errors.value.plantSpacingInches = e.plantSpacingInches;
		}

		return !Object.values(errors.value).some(Boolean);
	}

	function submitable(): boolean {
		touched.value.name = true;
		touched.value.cropId = true;
		touched.value.wetDate = true;
		touched.value.plantDate = true;
		touched.value.transplantDate = true;
		touched.value.acres = true;
		touched.value.harvestDates = true;
		return validate();
	}

	function touch<K extends keyof typeof touched.value>(k: K) {
		touched.value[k] = true;
	}

	/** Modal controls */
	function openModal(
		existing?: PlantingFormData | PlantingDTO,
		validateImmediately = false,
	) {
		// Set isOpen first to prevent any watchers from interfering
		isOpen.value = true;
		isBoundaryPopoverOpen.value = false;

		if (existing) {
			// Check if it's a PlantingDTO (has .id) or PlantingFormData
			if ("id" in existing && existing.id) {
				// It's a PlantingDTO - hydrate from it
				isEditing.value = true;
				hydrate(existing);
			} else {
				// It's a PlantingFormData - use directly
				isEditing.value = false;
				data.value = { ...(existing as PlantingFormData) };
				clearErrors();
				clearTouched();
				isNameCustomized.value = false;
				const existingName = (existing.name || "").trim();
				if (existingName && existingName !== suggestedName.value)
					isNameCustomized.value = true;
			}
		} else {
			// Creating new planting
			isEditing.value = false;
			hydrate(null);
		}

		// Seed the plastic color dropdown from whatever the form now holds. Only
		// canonical values pre-select; legacy values show nothing selected so they
		// aren't accidentally rewritten on save.
		plasticColor.value = getPlasticColorSelection(data.value.customData);
		initialPlasticColor.value = plasticColor.value;

		// Same idea for bed direction: only a canonical degree value pre-selects.
		selectedBedDirection.value = bedDirectionSelection(
			data.value.bedDirection,
		);
		initialBedDirection.value = selectedBedDirection.value;

		if (validateImmediately) validate();
	}

	function closeModal() {
		isOpen.value = false;
	}

	defineExpose({ openModal, closeModal });

	/** Boundary modal */
	const boundaryModalRef = ref<InstanceType<typeof BoundaryEditModal> | null>(
		null,
	);

	/**
	 * Calculate default center for boundary modal based on:
	 * 1. First selected lot's boundary (if exists)
	 * 2. Ranch center (if no lot but ranch selected)
	 * 3. undefined (falls back to customer center in BaseMap)
	 */
	const boundaryDefaultCenter = computed<[number, number] | undefined>(() => {
		// Priority 1: Use first selected lot's boundary center
		const firstLotId = data.value.lotIds[0];
		if (firstLotId) {
			const firstLot = lotsById.value?.[firstLotId];
			if (firstLot?.boundary) {
				const lotCenter = center(firstLot.boundary);
				return lotCenter.geometry.coordinates.slice(0, 2) as [
					number,
					number,
				];
			}
		}

		// Priority 2: Use ranch center (computed from all lots in ranch)
		if (data.value.ranchId) {
			const ranchBoundary = getRanchBoundary(data.value.ranchId);
			if (ranchBoundary) {
				return ranchBoundary.center.coordinates.slice(0, 2) as [
					number,
					number,
				];
			}
		}

		// Priority 3: undefined (BaseMap will use customer center or default)
		return undefined;
	});

	function openBoundaryModal() {
		boundaryModalRef.value?.openModal();
	}

	/** Boundary actions */

	/**
	 * Combines all selected lots' boundaries into a single geometry.
	 * Returns a Polygon for single lot, MultiPolygon for multiple lots.
	 */
	const combinedLotBoundary = computed<Polygon | MultiPolygon | undefined>(
		() => {
			const boundaries = data.value.lotIds
				.map((id) => lotsById.value?.[id]?.boundary)
				.filter((b): b is Polygon | MultiPolygon => !!b);

			if (boundaries.length === 0) return undefined;
			if (boundaries.length === 1) return boundaries[0];

			// Manually collect all polygon coordinates into a single MultiPolygon.
			// We avoid using turf's combine() because it groups by geometry type,
			const coordinates = boundaries.flatMap((b) =>
				b.type === "Polygon" ? [b.coordinates] : b.coordinates,
			);

			return multiPolygon(coordinates).geometry;
		},
	);

	/**
	 * Check if current boundary matches the combined lot boundary.
	 * Uses turf's booleanEqual for robust geometry comparison.
	 */
	const boundariesMatch = computed(() => {
		if (!combinedLotBoundary.value || !data.value.latestBoundary)
			return false;
		return booleanEqual(
			combinedLotBoundary.value,
			data.value.latestBoundary,
		);
	});

	/**
	 * Show "Restore Lot Boundary" when lots have boundaries AND
	 * the current boundary doesn't match.
	 */
	const canRestoreLotBoundary = computed(
		() => !!combinedLotBoundary.value && !boundariesMatch.value,
	);

	const hasBoundaryOptions = computed(
		() => canRestoreLotBoundary.value || !!data.value.latestBoundary,
	);

	const isBoundaryPopoverOpen = ref(false);

	/**
	 * Apply the combined lot boundary to the planting.
	 */
	function applyLotBoundary() {
		if (combinedLotBoundary.value) {
			data.value.latestBoundary = combinedLotBoundary.value;
			data.value.latestBoundaryType = BoundaryType.LotLevel;
			data.value.latestBoundaryDate = getCurrentDateString() as string;
		}
	}

	function applyClearBoundary() {
		data.value.latestBoundary = undefined;
		data.value.latestBoundaryType = undefined;
		data.value.latestBoundaryDate = getCurrentDateString() as string;
	}

	/**
	 * Restore lot boundary with confirmation if replacing existing boundary.
	 */
	async function restoreLotBoundary() {
		if (data.value.latestBoundary) {
			const alert = await alertController.create({
				header: "Restore Lot Boundary",
				message:
					"This will replace the current boundary with the lot boundary. This action cannot be undone.",
				buttons: [
					{ text: "Cancel", role: "cancel" },
					{
						text: "Restore",
						handler: () => applyLotBoundary(),
					},
				],
			});
			await alert.present();
		} else {
			applyLotBoundary();
		}
	}

	/**
	 * Auto-set boundary when lots are selected.
	 * Only auto-updates if:
	 * - No boundary exists yet (creating new planting), OR
	 * - Current boundary is LotLevel (auto-derived, so should stay in sync with lot selection)
	 * If the current boundary is UserCreated, it will not be auto-updated.
	 */
	watch(
		() => [...data.value.lotIds],
		(newLotIds) => {
			const shouldAutoUpdate =
				newLotIds.length > 0 &&
				combinedLotBoundary.value &&
				(!data.value.latestBoundary ||
					data.value.latestBoundaryType === BoundaryType.LotLevel);

			if (shouldAutoUpdate) {
				applyLotBoundary();
			}
		},
	);

	async function clearBoundary() {
		if (!data.value.latestBoundary) return;

		const alert = await alertController.create({
			header: "Clear Boundary",
			message:
				"This will remove the current boundary. This action cannot be undone.",
			buttons: [
				{ text: "Cancel", role: "cancel" },
				{
					text: "Clear",
					role: "destructive",
					handler: () => applyClearBoundary(),
				},
			],
		});
		await alert.present();
	}

	function clearHarvestDate() {
		data.value.harvestDates = [];
	}

	function handleBoundarySave(geometry: Polygon | MultiPolygon | null) {
		data.value.latestBoundary = geometry ?? undefined;
		data.value.latestBoundaryType = geometry
			? BoundaryType.UserCreated
			: undefined;
		data.value.latestBoundaryDate = getCurrentDateString() as string;
	}

	/** Save */
	const emit = defineEmits<{ save: [payload: PlantingFormData] }>();

	function submit() {
		if (!submitable()) return;

		// Only rewrite CustomData when the user actually changed the plastic color.
		// This preserves legacy/non-canonical values on untouched saves and avoids
		// spurious JSON-Patch ops on the edit path.
		if (
			isCalGiant.value &&
			plasticColor.value !== initialPlasticColor.value
		) {
			data.value.customData = setPlasticColor(
				data.value.customData,
				plasticColor.value,
			);
		}

		// Only rewrite bed direction when the user actually changed the dropdown, so
		// a legacy non-canonical degree value is preserved on an untouched save.
		if (selectedBedDirection.value !== initialBedDirection.value) {
			data.value.bedDirection = selectedBedDirection.value;
		}

		// Keep only the establishment date matching the method so a planting never
		// carries both a plant date and a transplant date.
		if (isTransplant.value) {
			data.value.plantDate = undefined;
		} else {
			data.value.transplantDate = undefined;
		}

		emit("save", { ...data.value });
	}
</script>

<style>
	/* This modal has a lot of content, so give it more room than Ionic's
	   default card size. Only override on the >=768px breakpoint where Ionic
	   renders the modal as a centered card; below that Ionic makes the modal
	   fill the screen and we must not clamp its height. The ion-content body
	   scrolls when content exceeds 90vh. */
	@media (min-width: 768px) {
		.planting-modal-expanded {
			--height: 90vh;
		}
	}
</style>
